import { Alert, Badge, Button, Card, Col, DateField, DatePicker, Descriptions, Divider, EditButton, Icons, Input, InputNumber, Row, Space, Tag, Tooltip, Typography, useDrawerForm, useModal } from '@pankod/refine-antd'
import { useTranslate } from '@pankod/refine-core'
import { DecreaseDrawer } from 'components/decrease/drawer'
import { EditOrderDetails } from 'components/orderDetails/edit'
import dayjs from 'dayjs'
import { definitions } from 'interfaces'
import { IssueDrawer } from 'pages'
import { ReactNode, useEffect, useState } from 'react'
import { MapEstimatedCostsStatusIDToText, checkIfInvoiceIsEditable, getOrderIdAlias, getRefundInvoicesByOrderId, itemsCompletedCounter, lockOrUnlockOrder, supabaseClient } from 'utility'
import { capitalizeFirstLetter } from 'utility/capitalizeFirstLetter'
import { getColorLabel } from 'utility/colorLabel'
import { ADMITTED_STATUS, API_URL, CANCELLED_STATUS, DEFAULT_DATE_FORMAT, DEFAULT_DATE_TIME_FORMAT, NO_STOCK_STATUS, ORDER_STATUS_ID_SUBSCRIPTION, PENALIZATION_STATUS, RETURN_STATUS } from 'utility/constants'
import { currency } from 'utility/currency'
import { DECREASE_TYPES_MAPPER, ISSUE_SUB_TYPES_REF_CANARY_MAPPER } from 'utility/mapper'
import { processNewStatus } from 'utility/orderPostActions'
import { CommentModal } from './commentsModal'
import { HistoryModal } from './historyModal'
import { RefundDrawer } from './refundDrawer'
import { WarehouseNotesModal } from './WarehouseNotesModal'

type OrderDetailsProps = {
  order: definitions['Order'] & { issues?: definitions['issues'][] } | undefined
  refetchFn: () => void
  warehouseMode?: boolean
  warehouseDecisionContent?: ReactNode
  warehouseActionContent?: ReactNode
};

export const OrderDetails: React.FC<OrderDetailsProps> = (props) => {
  const {
    formProps: editFormProps,
    drawerProps: editDrawerProps,
    show: editShow,
    saveButtonProps: editSaveButtonProps,
    close: editClose,
    id
  } = useDrawerForm<definitions['orders']>({
    action: 'edit',
    resource: 'orders',
    redirect: false
  })

  const {
    drawerProps: createIssueDrawerProps,
    formProps: createIssueFormProps,
    show: createIssueShow,
    close: createIssueClose
  } = useDrawerForm<definitions['issues']>({
    action: 'create',
    resource: 'issues',
    redirect: false
  })

  const {
    drawerProps: editdecreaseDrawerProps,
    formProps: editdecreaseFormProps,
    show: editdecreaseShow,
    close: editdecreaseClose
  } = useDrawerForm<definitions['issues']>({
    action: 'edit',
    resource: 'orders',
    redirect: false
  })

  const {
    drawerProps: refundDrawerProps,
    formProps: refundDrawerFormProps,
    show: refundDrawerShow,
    close: refundDrawerClose
  } = useDrawerForm<definitions['refunds']>({
    action: 'create',
    resource: 'refunds',
    redirect: false
  })

  const t = useTranslate()
  let order = props?.order

  let orderFinalTotal = order?.total ?? 0
  const historicButtonAction = t('buttons.viewOrderHistory')
  // const regenerateInvoiceAction = t('buttons.regenerateInvoice')
  let volumetricSupplements = 0
  let nonVolumetricSupplements = 0
  let refundTransportSupplements = 0
  let refundSupplements = 0
  let shippingPrice = order?.shipping_price || 0

  if (order) {
    volumetricSupplements = order.supplements
      .filter(s => s.volumetric)
      .map(s => s.supplement || 0)
      .reduce((a, b) => a + b, 0)

    nonVolumetricSupplements = order.supplements
      .filter(s => !s.volumetric &&
        s.description !== 'reducedIva' &&
        s.description !== 'reducedIvaSecondHand' &&
        s.description !== 'reducedIvaBreakdown' &&
        s.description !== 'reducedIvaEU' &&
        s.description !== 'reducedIvaShipment' &&
        s.description !== 'refund' &&
        s.description !== 'refundStore' &&
        s.description !== 'refundByCancelation' &&
        s.description !== 'refundTransport' &&
        s.description !== 'transportToPeninsula' &&
        s.description !== 'transportSupplement' &&
        s.description !== 'unified' &&
        s.description !== 'igic' &&
        s.description !== 'shippingInsurancePrice' &&
        s.description !== 'priceChange')
      .map(s => s.supplement || 0)
      .reduce((a, b) => a + b, 0)

    refundTransportSupplements = order.supplements
      .filter(s => !s.volumetric && !s.deleted && (s.description === 'refundTransport' || s.description === 'transportToPeninsula' || s.description === 'transportSupplement'))
      .map(s => s.supplement || 0)
      .reduce((a, b) => a + b, 0)

    refundSupplements = order.supplements
      .filter(s => !s.volumetric && !s.deleted && (s.description === 'refund' || s.description === 'refundStore'))
      .map(s => s.supplement || 0)
      .reduce((a, b) => a + b, 0)

    // refundByCancelation is a special case, we don't need it in the summary

    orderFinalTotal += volumetricSupplements + nonVolumetricSupplements + refundTransportSupplements + refundSupplements
    shippingPrice += refundTransportSupplements
  }

  const initialTotal = (order?.syniva_price ?? 0) + (order?.shipping_insurance_price || 0) + shippingPrice + (order?.igic || 0) + ((order?.marketplace_id?.id === 0) ? order?.custom_order_price || 0 : 0) + (order?.subscription_cost ?? 0)

  const { modalProps: modalPropsComment, show: showComment, close: closeComment } = useModal()
  const { modalProps: modalPropsWarehouseNotes, show: showWarehouseNotes, close: closeWarehouseNotes } = useModal()
  const { modalProps: modalPropsHistory, show: showHistory, close: closeHistory } = useModal()
  const [editAtypical, setEditAtypical] = useState(false)
  const [currentAtypical, setCurrentAtypical] = useState<number | undefined | null>(order?.atypical)
  const [editPackageNumber, setEditPackageNumber] = useState(false)
  const [currentPackageNumber, setCurrentPackageNumber] = useState<number>(order?.package_number)
  const [editLocation, setEditLocation] = useState(false)
  const [editEstimatedDate, setEditEstimatedDate] = useState(false)
  const [currentLocation, setCurrentLocation] = useState<string>(order?.location)
  const [currentEstimatedDate, setCurrentEstimatedDate] = useState<string | undefined | null>(order?.estimated_delivered_date)
  const [loading, setLoading] = useState<boolean>(false)
  const [isPenalization, setIsPenalization] = useState<boolean>(false)
  const [isCorrection, setIsCorrection] = useState<boolean>(false)
  const [isRefund, setIsRefund] = useState<boolean>(false)

  const canUseActions = [CANCELLED_STATUS, NO_STOCK_STATUS, PENALIZATION_STATUS, RETURN_STATUS].includes(order?.status.id!)

  const [editable, setEditable] = useState<boolean>(false)
  const [refundInvoices, setRefundInvoices] = useState<definitions['refunds_invoices'][]>([])

  const retrieveRefundInvoices = async (orderId: number) => {
    const data = await getRefundInvoicesByOrderId(orderId)
    if (data) {
      setRefundInvoices(data)
    }
  }

  useEffect(() => {
    const fetchEditableStatus = async () => {
      // Check if the invoice is editable before making changes
      const { editable } = await checkIfInvoiceIsEditable(order?.id)
      setEditable(editable)
    }

    if (order?.id) {
      fetchEditableStatus()
      retrieveRefundInvoices(order?.id)
    }
  }, [order?.id])

  const canNotRegenerate = !!(
    !(
      order && order.status_id && // has status id
      (order.status_id >= ADMITTED_STATUS || order.status_id === RETURN_STATUS) && // and the status greater or equal to admitted or is return
      ![CANCELLED_STATUS, NO_STOCK_STATUS].includes(order.status_id) // and the status is not cancelled or out of stock
    ) ||
    !order?.invoices?.invoice_number || // or the order does not have an invoice number
    !editable || // or the order is not editable
    refundInvoices.length > 0 // or the order has a refund/correction invoice
  )

  const handleEditAtypical = (isClose: boolean = false) => {
    if (isClose) {
      setCurrentAtypical(order?.atypical)
      setEditAtypical(false)
    } else {
      setEditAtypical(!editAtypical)
      setEditPackageNumber(false)
      setEditLocation(false)
      setEditEstimatedDate(false)
    }
  }

  const handleEditPackageNumber = (isClose: boolean = false) => {
    if (isClose) {
      setCurrentPackageNumber(order?.package_number)
      setEditPackageNumber(false)
    } else {
      setEditPackageNumber(!editPackageNumber)
      setEditLocation(false)
      setEditEstimatedDate(false)
      setEditAtypical(false)
    }
  }

  const handleEditLocation = (isClose: boolean = false) => {
    if (isClose) {
      setCurrentLocation(order?.location)
      setEditLocation(false)
    } else {
      setEditLocation(!editLocation)
      setEditPackageNumber(false)
      setEditEstimatedDate(false)
      setEditAtypical(false)
    }
  }

  const handleEstimatedDate = (isClose: boolean = false) => {
    if (isClose) {
      setCurrentEstimatedDate(order?.estimated_delivered_date)
      setEditEstimatedDate(false)
    } else {
      setEditEstimatedDate(!editLocation)
      setEditPackageNumber(false)
      setEditLocation(false)
      setEditAtypical(false)
    }
  }

  const onChangeAtypical = (value: number) => {
    setCurrentAtypical(value)
  }

  const onChangePackageNumber = (value: number) => {
    setCurrentPackageNumber(value)
  }

  const onChangeLocation = (value: any) => {
    setCurrentLocation(value)
  }

  const onChangeEstimatedDate = (value: any) => {
    if (value === null) {
      setCurrentEstimatedDate(null)
      handleSave()
    } else {
      const date = new Date(dayjs(value).toISOString())
      setCurrentEstimatedDate(date.toISOString())
    }
  }

  const handleSave = async () => {
    if (order?.id) {
      await supabaseClient.from('orders')
        .update({
          atypical: currentAtypical,
          location: (currentLocation === null || currentLocation === '' || currentLocation === undefined) ? null : currentLocation,
          package_number: currentPackageNumber,
          estimated_delivered_date: currentEstimatedDate,
          updated_by: supabaseClient.auth.user()?.email
        })
        .eq('id', order?.id)
      updateOrderValue()
    }
    setEditPackageNumber(false)
    setEditLocation(false)
    setEditEstimatedDate(false)
    setEditAtypical(false)
  }

  const updateOrderComments = (comments: string) => {
    if (order) {
      const settedOrder = Object.assign(order)
      settedOrder.comments = (comments === '') ? null : comments
      order = Object.assign(settedOrder)
    }
  }

  const updateOrderWarehouseNote = (note: string) => {
    if (order) {
      const settedOrder = Object.assign(order)
      settedOrder.warehouseNote = (note === '') ? null : note
      order = Object.assign(settedOrder)
    }
  }

  const updateOrderValue = () => {
    if (order) {
      const settedOrder = Object.assign(order)
      settedOrder.package_number = currentPackageNumber
      settedOrder.location = currentLocation
      order = Object.assign(settedOrder)
    }
  }

  const handlePriority = async () => {
    if (order?.id) {
      setLoading(true)
      await supabaseClient.from('orders')
        .update({
          is_priority: !(order?.is_priority),
          updated_by: supabaseClient.auth.user()?.email
        })
        .eq('id', order?.id)
      props.refetchFn()
      setLoading(false)
    }
  }

  const handleQuitEdited = async () => {
    await supabaseClient
      .from('orders')
      .update({
        edited: false,
        updated_by: supabaseClient.auth.user()?.email
      })
      .eq('id', order?.id)

    window.location.reload()
  }

  const ivaResolutionLabels: Record<string, string> = {
    process: 'Procesar normalmente',
    change_to_reduced: 'Pasar a estándar reducido',
    add_iva_supplement: 'Pasar a ajuste de IVA'
  }
  const ivaResolution = order?.iva_status ? ivaResolutionLabels[order.iva_status] : undefined
  const affectedItems = order?.intra_iva_affected_items?.trim()
  const activeItemCount = order?.items?.filter(item => !item.deleted && !item.disabled).length

  return (
    <>
      <Row gutter={[15, 15]} style={props.warehouseMode ? { alignItems: 'stretch' } : { height: '100%' }}>
        <Col xl={13} xs={24} style={props.warehouseMode ? { display: 'flex' } : undefined}>
          <Card size={props.warehouseMode ? 'small' : 'default'} style={props.warehouseMode ? { width: '100%', minHeight: 0 } : undefined}>
            {!props.warehouseMode && <EditButton onClick={() => editShow(id)} style={{ marginTop: 0, float: 'right' }}>
              {t('order.actions.editDetails')}
            </EditButton>}
            <Descriptions title={t('order.details')} labelStyle={{ fontWeight: 'bold' }} column={1}>
              <Descriptions.Item label={t('order.fields.id')}>
                <div style={{ backgroundColor: `${(order?.is_priority) ? 'rgba(255, 215, 0, 0.19)' : 'transparent'}`, display: 'flex', alignItems: 'center', textAlign: 'center', gap: '1rem' }}>
                  {order?.id}
                  {order?.is_priority && <Icons.ExclamationOutlined style={{ fontSize: '20px', color: 'error' }} />}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label={t('order.fields.alias')}>{getOrderIdAlias(order?.id || 0)}</Descriptions.Item>
              {!props.warehouseMode && <Descriptions.Item label={t('order.fields.administrator')}>{order?.administrator?.name}</Descriptions.Item>}
              <Descriptions.Item label={t('order.fields.status')}><Tag color={getColorLabel(order?.status?.name, order?.prev_status_id)}>{capitalizeFirstLetter(order?.status?.name)}</Tag></Descriptions.Item>
              <Descriptions.Item label={t('order.fields.type')}>{order?.type}</Descriptions.Item>
              {!props.warehouseMode && <Descriptions.Item label={t('order.fields.paymentMethod')}>{order?.payment_method?.name}</Descriptions.Item>}
              <Descriptions.Item label={t('order.fields.shipmentMethod')}>{order?.shipping_method?.name}</Descriptions.Item>
              <Descriptions.Item label={t('order.fields.carrier')}>{order?.shipments[0]?.shipped_by || ''}</Descriptions.Item>
              {!props.warehouseMode && order?.estimated_costs_status && <Descriptions.Item label={t('order.fields.estimatedStatus')}>{MapEstimatedCostsStatusIDToText(order.estimated_costs_status)}</Descriptions.Item>}

              <Descriptions.Item label={t('order.fields.itemsCompleted')}>{itemsCompletedCounter(order?.items as any)}</Descriptions.Item>

              {(!editPackageNumber)
                ? <Descriptions.Item label={t('order.fields.package_number')}>
                    <div style={{ display: 'flex', alignItems: 'initial' }}>
                      {currentPackageNumber}
                      <Button shape='circle' style={{ border: 'none', top: -5, marginLeft: 20 }} icon={<Icons.EditOutlined />} onClick={() => handleEditPackageNumber()} />
                    </div>
                  </Descriptions.Item>
                : <Descriptions.Item label={t('order.fields.package_number')}>
                    <div style={{ display: 'flex', alignItems: 'initial' }}>
                      <InputNumber style={{ minWidth: '50px', maxWidth: '100px', height: '32px', marginLeft: 10, marginTop: -5 }} defaultValue={currentPackageNumber} onChange={onChangePackageNumber}/>
                      <Button shape='circle' style={{ border: 'none', top: -5, marginLeft: 10 }} icon={<Icons.SaveOutlined />} onClick={handleSave} />
                      <Button shape='circle' style={{ border: 'none', top: -5, marginLeft: 0 }} icon={<Icons.CloseOutlined />} onClick={() => handleEditPackageNumber(true)} />
                    </div>
                  </Descriptions.Item>}
              {(!editLocation)
                ? <Descriptions.Item label={t('order.fields.location')}>
                    <div style={{ display: 'flex', alignItems: 'initial' }}>
                      {currentLocation}
                      <Button shape='circle' style={{ border: 'none', top: -5, marginLeft: 20 }} icon={<Icons.EditOutlined />} onClick={() => handleEditLocation()} />
                      {order?.completed ? <b key={order.completed} style={{ marginBottom: '0px', marginLeft: '1rem' }}>{t('order.biReport.purchasesCompleted')}</b> : null}
                    </div>
                  </Descriptions.Item>
                : <Descriptions.Item label={t('order.fields.location')}>
                    <div style={{ display: 'flex', alignItems: 'initial' }}>
                      <Input style={{ minWidth: '50px', maxWidth: '100px', height: '32px', marginLeft: 10, marginTop: -5 }} defaultValue={currentLocation} onChange={(e) => onChangeLocation(e.target.value)}/>
                      <Button shape='circle' style={{ border: 'none', top: -5, marginLeft: 10 }} icon={<Icons.SaveOutlined />} onClick={handleSave} />
                      <Button shape='circle' style={{ border: 'none', top: -5, marginLeft: 0 }} icon={<Icons.CloseOutlined />} onClick={() => handleEditLocation(true)} />
                    </div>
                  </Descriptions.Item>}

              <Descriptions.Item label={t('order.fields.createdAt')}><DateField value={order?.created_at} format={DEFAULT_DATE_TIME_FORMAT} /></Descriptions.Item>
              <Descriptions.Item label={t('order.fields.updatedAt')}><DateField value={order?.updated_at} format={DEFAULT_DATE_TIME_FORMAT} /></Descriptions.Item>

              {(!editEstimatedDate)
                ? <Descriptions.Item label={t('order.fields.estimated_delivered_date')}>
                    <div style={{ display: 'flex', alignItems: 'initial' }}>
                      {currentEstimatedDate !== null ? <DateField value={currentEstimatedDate} format={DEFAULT_DATE_FORMAT} /> : ''}
                      <Button shape='circle' style={{ border: 'none', top: -5, width: '25px' }} icon={<Icons.EditOutlined />} onClick={() => handleEstimatedDate()} />
                      <Button shape='circle' style={{ border: 'none', top: -5, width: '25px' }} icon={<Icons.DeleteOutlined />} onClick={() => onChangeEstimatedDate(null)} />
                    </div>
                  </Descriptions.Item>
                : <Descriptions.Item label={t('order.fields.estimated_delivered_date')}>
                    <div style={{ display: 'flex', alignItems: 'initial' }}>
                      {/* <Input style={{ minWidth: '50px', maxWidth: '100px', height: '32px', marginLeft: 10, marginTop: -5 }} defaultValue={currentEstimatedDate} onChange={(e) => onChangeEstimatedDate(e.target.value)}/> */}
                      <DatePicker onChange={onChangeEstimatedDate} />
                      <Button shape='circle' style={{ border: 'none', top: -5 }} icon={<Icons.SaveOutlined />} onClick={handleSave} />
                      <Button shape='circle' style={{ border: 'none', top: -5 }} icon={<Icons.CloseOutlined />} onClick={() => handleEstimatedDate(true)} />
                    </div>
                  </Descriptions.Item>}
            </Descriptions>
            {!props.warehouseMode && <Space style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
              <Button
                onClick={showHistory}>
                  {historicButtonAction}
              </Button>
            </Space>}
            {!props.warehouseMode && <Space style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
              <Button style={{ backgroundColor: `${(order?.issues?.[0]?.id) ? ((order.issues[0].is_opened) ? '#f0e68c' : '#a9fd69') : 'transparent'}` }}
                onClick={() => {
                  // if (order?.issues?.[order?.issues.length - 1]?.id) {
                  //   window.open(`/issues/show/${order?.issues?.[order?.issues.length - 1]?.id}`, '_blank', 'noopener,noreferrer')
                  // } else {
                  createIssueShow()
                  // }
                }}
              >
                {/* {t(`issues.titles.${order?.issues?.[0]?.id ? 'show' : 'create'}`)} */}
                {t('issues.titles.create')}
              </Button>
            </Space>}
            {!props.warehouseMode && <Space style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
              <Button loading={loading} onClick={() => handlePriority()}>
                {t(`order.actions.${(order?.is_priority) ? 'setNotPriority' : 'setIsPriority'}`)}
              </Button>
            </Space>}
            {/* {(userEmail && //See if the user is admin too// && order?.invoice_date && order?.invoice) && <Space style={{ display: 'flex', justifyContent: 'center', margin: '0.5rem 0 0.5rem 0' }}>
              <Button style={{ backgroundColor: '#f0e68c' }}
                onClick={async () => {
                  window.open(`${API_URL}/billings/pdf/${order?.id}`, '_blank', 'noopener,noreferrer')
                }}>
                  {regenerateInvoiceAction}
              </Button>
            </Space>} */}

            <div style={{ height: props.warehouseMode ? 0 : '2rem' }} />
            <Divider style={props.warehouseMode ? { display: 'none' } : undefined} />
            <div style={{ display: props.warehouseMode ? 'none' : 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {!props.warehouseMode && <Tooltip title={t('common.generalTooltipNote')}>
                  <span style={{ cursor: 'pointer' }} onClick={showComment}>
                    <Badge
                      style={{ fontSize: 20 }}
                      count={
                        <Icons.WarningOutlined style={{ color: order?.comments ? '#f5222d' : 'grey' }} />
                      }
                    />
                  </span>
                </Tooltip>}
                {!props.warehouseMode && <span style={{ cursor: 'pointer' }} onClick={showWarehouseNotes}>
                  <Space style={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      danger={!!order?.warehouseNote?.trim()}
                    >
                      {t('common.warehouseTooltipNote')}
                    </Button>
                  </Space>
                </span>}
              </div>
              {!props.warehouseMode && <span
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  lockOrUnlockOrder(order?.id, !order?.locked)
                  window.location.reload()
                }}
              >
                <Badge
                  style={{ fontSize: 20 }}
                  count={
                    order?.locked
                      ? <Icons.LockOutlined style={{ color: '#f5222d' }} />
                      : <Icons.UnlockOutlined style={{ color: '#21bf4b' }} />
                  }
                />
              </span>}
            </div>
            {!props.warehouseMode && ivaResolution && (
              <Alert
                message={<strong>{ivaResolution}</strong>}
                description={order?.iva_status === 'add_iva_supplement' && (affectedItems || activeItemCount !== 1)
                  ? <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      Artículos afectados: {affectedItems || 'No especificados en el aviso'}
                    </div>
                  : order?.iva_status === 'change_to_reduced' ? 'Todo el pedido' : undefined}
                type='info'
                showIcon
                style={{ marginTop: 16, backgroundColor: '#e6f7ff', borderColor: '#91d5ff' }}
              />
            )}
          </Card>
        </Col>

        <Col xl={11} xs={24} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Card
            bordered={false}
            size={props.warehouseMode ? 'small' : 'default'}
            style={props.warehouseMode ? { flex: 1, display: 'flex', flexDirection: 'column' } : { flex: '2' }}
            bodyStyle={props.warehouseMode ? { flex: 1, display: 'flex', flexDirection: 'column' } : undefined}
          >

              {(order?.cancelation_date)
                ? <Row justify='space-between' style={{ backgroundColor: '#f0e68c', borderRadius: '5px', margin: '-10px' }}>
                  <Typography style={{ padding: 10, fontWeight: 'bold' }}>{t('order.fields.cancelRequest')}:</Typography>
                  <div style={{ display: 'flex' }}>
                    <Col style={{ padding: 10 }}>
                      <Typography style={{ padding: 0, fontWeight: 'bold' }}>{t('order.fields.date')}:</Typography>
                      {(order?.cancelation_reason) && <Typography style={{ padding: 0, fontWeight: 'bold' }}>{t('order.fields.cancelReasonReturn')}:</Typography>}
                    </Col>
                    <Col style={{ padding: 10 }}>
                      <DateField value={order?.cancelation_date} format={DEFAULT_DATE_FORMAT} />
                      {(order?.cancelation_reason) && <Typography style={{ padding: 0 }}>{t(`order.fields.options.${order?.cancelation_reason}`)}</Typography>}
                    </Col>
                  </div>
                </Row>
                : null}

              {(order?.return_date || (order?.issues && order.issues.length > 0))
                ? (order?.issues && order.issues.length > 0)
                    ? <Row justify='space-between' style={{ backgroundColor: '#f0e68c', borderRadius: '5px', margin: '-10px', marginBottom: '5px' }}>
                      {order.issues
                        .sort((a: definitions['issues'], b: definitions['issues']) => b.id - a.id)
                        .map((issue) => {
                          const issueSubTypeLabel = issue.issue_sub_type_id
                            ? ISSUE_SUB_TYPES_REF_CANARY_MAPPER[issue.issue_sub_type_id as keyof typeof ISSUE_SUB_TYPES_REF_CANARY_MAPPER]
                            : undefined

                          return (
                            <div key={issue.id} style={{ width: '100%' }}>
                              <div style={{ display: 'flex', alignItems: 'center', padding: '10px' }}>
                                <span style={{ color: issue.is_opened ? '#21bf4b' : '#f5222d' }}>
                                  {issue.is_opened ? <Icons.UnlockOutlined /> : <Icons.LockOutlined />}
                                </span>
                                <Typography style={{ padding: 10, fontWeight: 'bold' }}>
                                  {t('order.fields.incidenceNumber', { orderId: issue.id })}:
                                </Typography>
                              </div>
                              <div style={{ display: 'flex', width: '100%' }}>
                                <Col style={{ padding: 10, width: '25%' }}>
                                  <Typography style={{ padding: 0, fontWeight: 'bold' }}>{t('order.fields.date')}:</Typography>
                                  {(issue.issue_sub_type_id) && <Typography style={{ padding: 0, fontWeight: 'bold' }}>{t('order.fields.cancelReasonReturn')}:</Typography>}
                                </Col>
                                <Col style={{ padding: 10, width: '60%' }}>
                                  <DateField value={issue?.created_at} format={DEFAULT_DATE_FORMAT} />
                                  {(issueSubTypeLabel) && <Typography style={{ padding: 0 }}>{t(`issues.issue_sub_types.${issueSubTypeLabel}`)}</Typography>}
                                </Col>
                                <div style={{ width: '15%', marginTop: '1rem' }}>
                                  <Button onClick={() => window.open(`/issues/show/${issue.id}`, '_blank', 'noopener,noreferrer')}>
                                    <Icons.EyeOutlined />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )
                        })
                      }
                    </Row>
                    : <Row justify='space-between' style={{ backgroundColor: '#f0e68c', borderRadius: '5px', margin: '-10px' }}>
                      <Typography style={{ padding: 10, fontWeight: 'bold' }}>{t('order.fields.returnRequest')}:</Typography>
                      <div style={{ display: 'flex' }}>
                        <Col style={{ padding: 10 }}>
                          <Typography style={{ padding: 0, fontWeight: 'bold' }}>{t('order.fields.date')}:</Typography>
                          {(order?.return_reason) && <Typography style={{ padding: 0, fontWeight: 'bold' }}>{t('order.fields.cancelReasonReturn')}:</Typography>}
                        </Col>
                        <Col style={{ padding: 10 }}>
                          <DateField value={order?.return_date} format={DEFAULT_DATE_FORMAT} />
                          {(order?.return_reason) && <Typography style={{ padding: 0 }}>{order?.return_reason}</Typography>}
                        </Col>
                      </div>
                    </Row>
                : null}

            {(order?.cancelation_date || order?.return_date) ? <Divider style={{ margin: '10px 0px' }} /> : null}

            {props.warehouseMode && props.warehouseDecisionContent}

            {props.warehouseMode && <Space style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'auto', paddingTop: 10 }}>
              <Button onClick={showHistory}>
                {historicButtonAction}
              </Button>
              <Button style={{ backgroundColor: `${(order?.issues?.[0]?.id) ? ((order.issues[0].is_opened) ? '#f0e68c' : '#a9fd69') : 'transparent'}` }}
                onClick={() => createIssueShow()}
              >
                {t('issues.titles.create')}
              </Button>
              {props.warehouseActionContent}
            </Space>}

            {!props.warehouseMode && <>
            <Descriptions title={t('order.summary')} labelStyle={{ fontWeight: 'bold' }} contentStyle={{ position: 'absolute', right: 15 }} column={1}>
              <Descriptions.Item style={{ padding: 0 }} label={t('order.fields.subtotal')}>{currency(order?.subtotal)}</Descriptions.Item>
              <Descriptions.Item style={{ padding: 0 }} label={t('order.fields.synivaPrice')}>{currency(order?.syniva_price)}</Descriptions.Item>
              <Descriptions.Item style={{ padding: 0 }} label={t('order.fields.totalSaving')}>{currency(order?.total_saving)}</Descriptions.Item>
              <Descriptions.Item style={{ padding: 0 }} label={t('order.fields.shippingPrice')}>{currency(shippingPrice)}</Descriptions.Item>
              <Descriptions.Item style={{ padding: 0 }} label={t('order.fields.shippingInsurance')}>{currency(order?.shipping_insurance_price)}</Descriptions.Item>
              <Descriptions.Item style={{ padding: 0 }} label={t('order.fields.igic')}>{currency(order?.igic)}</Descriptions.Item>
              {(order?.marketplace_id?.id === 0) && <Descriptions.Item style={{ padding: 0 }} label={t('order.fields.customOrderPrice')}>{currency(order?.custom_order_price)}</Descriptions.Item>}
              {(order?.subscription_cost && order.subscription_cost > 0) && <Descriptions.Item style={{ padding: 0 }} label={t('order.fields.subscribedOrder')}>{currency(order?.subscription_cost)}</Descriptions.Item>}
              <Descriptions.Item style={{ padding: 0 }} label={t('order.fields.initial_total')}>{currency(initialTotal)}</Descriptions.Item>
              <Descriptions.Item style={{ padding: 0 }} label={`${t('order.fields.discount')} ${(order?.promotional_code) ? '(' + order?.promotional_code + ')' : ''}`} >
                {(order?.discount && order?.discount > 0) ? '-' : ''}{currency(order?.discount)}
              </Descriptions.Item>
              <Descriptions.Item style={{ padding: 0 }} label={t('order.fields.walletUsed')}>-{currency(order?.wallet_amount_used)}</Descriptions.Item>
            </Descriptions>

            <Divider style={{ margin: '10px 0px' }} />

            <Descriptions labelStyle={{ fontWeight: 'bold' }} contentStyle={{ position: 'absolute', right: 15 }} column={1}>
              <Descriptions.Item style={{ padding: 0 }} label={t('order.fields.supplements')}>{currency(nonVolumetricSupplements)}</Descriptions.Item>
              <Descriptions.Item style={{ padding: 0 }} label={t('order.fields.volumetricSupplements')}>{currency(volumetricSupplements)}</Descriptions.Item>
              <Descriptions.Item style={{ padding: 0 }} label={t('order.fields.supRefunds')}>{currency(refundSupplements)}</Descriptions.Item>
              <Descriptions.Item style={{ padding: 0 }} label={t('order.fields.total')}>{currency(orderFinalTotal)}</Descriptions.Item>
            </Descriptions>

            {(order?.initial_payment) && <>
              <Divider style={{ margin: '10px 0px' }} />

              <Descriptions labelStyle={{ fontWeight: 'bold' }} contentStyle={{ position: 'absolute', right: 15 }} column={1}>
                <Descriptions.Item style={{ padding: 0 }} label={t('order.fields.initial_payment')}>{currency(order?.initial_payment)}</Descriptions.Item>
              </Descriptions>
            </>}

            {(order?.sequra_disbursement_ref) && <>
              <Divider style={{ margin: '10px 0px' }} />

              <Descriptions labelStyle={{ fontWeight: 'bold' }} contentStyle={{ position: 'absolute', right: 15 }} column={1}>
                <Descriptions.Item style={{ padding: 0 }} label={t('order.fields.disbursement')}>{order?.sequra_disbursement_ref}</Descriptions.Item>
              </Descriptions>
            </>}

            <div style={{ margin: '3rem 0px' }}>
              <Divider />

              {(!editAtypical)
                ? <Descriptions labelStyle={{ fontWeight: 'bold' }} contentStyle={{ position: 'absolute', right: 15 }} column={1}>
                  <Descriptions.Item label={t('order.fields.atypical')}>
                      <div style={{ display: 'flex', alignItems: 'initial' }}>
                        {(currentAtypical) ? currency(currentAtypical) : ''}
                        <Button shape='circle' style={{ border: 'none', top: -5, marginLeft: 20 }} icon={<Icons.EditOutlined />} onClick={() => handleEditAtypical()} />
                      </div>
                  </Descriptions.Item>
                </Descriptions>
                : <Descriptions labelStyle={{ fontWeight: 'bold' }} contentStyle={{ position: 'absolute', right: 15 }} column={1}>
                  <Descriptions.Item label={t('order.fields.atypical')}>
                    <div style={{ display: 'flex', alignItems: 'initial' }}>
                      <InputNumber decimalSeparator=',' style={{ minWidth: '50px', maxWidth: '100px', height: '32px', marginLeft: 10, marginTop: -5 }} defaultValue={currentAtypical || undefined} onChange={onChangeAtypical}/>
                      <Button shape='circle' style={{ border: 'none', top: -5, marginLeft: 10 }} icon={<Icons.SaveOutlined />} onClick={handleSave} />
                      <Button shape='circle' style={{ border: 'none', top: -5, marginLeft: 0 }} icon={<Icons.CloseOutlined />} onClick={() => handleEditAtypical(true)} />
                    </div>
                  </Descriptions.Item>
                </Descriptions>}

              {(order?.decrease_costs) && <>
                <Descriptions labelStyle={{ fontWeight: 'bold' }} contentStyle={{ position: 'absolute', right: 15 }} column={1}>
                  <Descriptions.Item style={{ padding: 0 }} label={t('decrease.decrease_costs')}>{currency(order?.decrease_costs)}</Descriptions.Item>
                </Descriptions>
                <Descriptions labelStyle={{ fontWeight: 'bold' }} contentStyle={{ position: 'absolute', right: 15 }} column={1}>
                  <Descriptions.Item style={{ padding: 0 }} label={t('decrease.decrease_types')}>{''}</Descriptions.Item>
                </Descriptions>
                <ul style={{ paddingLeft: '-1rem' }}>
                  <li>{t(`decrease_types.${(DECREASE_TYPES_MAPPER as any)[order?.decrease_types as number]}`)}</li>
                </ul>
              </>}
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button onClick={() => editdecreaseShow() }>
                  {t('decrease.decrease_button')}
                </Button>
              </div>
              {/* <Descriptions style={{ marginTop: '3px' }} labelStyle={{ fontWeight: 'bold' }} contentStyle={{ position: 'absolute', right: 15 }} column={1}>
                <Descriptions.Item style={{ padding: 0 }} label={t('refund_status.refunds')}>{null}</Descriptions.Item>
              </Descriptions>
              <div style={{ overflowY: 'auto', height: '98px', marginTop: '8px' }}>
                  {order?.refunds.map((refund) => {
                    if (refund.status_id === 0) {
                      return (
                          <p key={refund.id}>{refund.description} --- {t(`refund_status.${REFUNDS_STATUS_MAPPER[refund.status_id]}`)}</p>
                      )
                    } else {
                      return null
                    }
                  })}
              </div> */}
            </div>
            </>}
          </Card>

          {!props.warehouseMode && (
            (order?.status_id !== ORDER_STATUS_ID_SUBSCRIPTION)
              ? (
                <Card bordered={false}>
                  <Typography.Text strong>{t('common.actions')}</Typography.Text>
                  <Space style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                    <Button disabled={canUseActions} loading={loading} style={{ width: '150px' }} onClick={async () => {
                      setLoading(true)
                      await processNewStatus(CANCELLED_STATUS, order!)
                      setLoading(false)
                    }}>{t('actions.cancel')}</Button>
                    <Button disabled={canUseActions} loading={loading} style={{ width: '150px' }} onClick={async () => {
                      setLoading(true)
                      await processNewStatus(NO_STOCK_STATUS, order!)
                      setLoading(false)
                    }}>{t('actions.noStock')}</Button>
                  </Space>
                  <Space style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                    <Button disabled={canUseActions} style={{ width: '150px' }} onClick={() => {
                      setIsPenalization(true)
                      setIsCorrection(false)
                      setIsRefund(false)
                      refundDrawerShow()
                    }}>{t('actions.penalization')}</Button>
                    <Button style={{ width: '150px' }} onClick={() => {
                      setIsPenalization(false)
                      setIsCorrection(false)
                      setIsRefund(false)
                      refundDrawerShow()
                    }}>{t('actions.return')}</Button>
                  </Space>
                  <Divider />
                  <Space style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                    <Button disabled={canNotRegenerate} style={{ width: '150px' }} onClick={() => {
                      window.open(`${API_URL}/billings/pdf/${order?.id}`, '_blank', 'noopener,noreferrer')
                    }}>
                      {t('buttons.regenerateInvoice')}
                    </Button>
                    <Button disabled={!order?.invoices?.invoice_number} style={{ width: '150px' }} onClick={() => {
                      setIsPenalization(false)
                      setIsCorrection(true)
                      setIsRefund(false)
                      refundDrawerShow()
                    }}>
                      {t('buttons.correctionInvoice')}
                    </Button>
                  </Space>
                  <Space style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                    <Button style={{ width: '150px' }} onClick={() => {
                      setIsPenalization(false)
                      setIsCorrection(false)
                      setIsRefund(true)
                      refundDrawerShow()
                    }}>
                      {t('buttons.refund')}
                    </Button>
                    <Button disabled={!order?.edited || !order?.invoices?.reviewed} style={{ width: '150px' }} onClick={() => {
                      handleQuitEdited()
                    }}>
                      {t('buttons.doNotCorrection')}
                    </Button>
                  </Space>
                </Card>
                )
              : (
                <Card bordered={false}>
                  <Typography.Text strong>{t('common.actions')}</Typography.Text>
                    <Space style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                      <Button
                        style={{ width: '150px' }}
                        onClick={() => {
                          setIsPenalization(false)
                          setIsCorrection(false)
                          setIsRefund(false)
                          refundDrawerShow()
                        }}
                      >
                        {t('actions.return')}
                      </Button>
                  </Space>
                </Card>
                )
          )}
        </Col>
      </Row>
      <EditOrderDetails
        drawerProps={editDrawerProps}
        formProps={editFormProps}
        saveButtonProps={editSaveButtonProps}
        closeDrawer={editClose}
        order={order}
      />
      <CommentModal
        modalProps={modalPropsComment}
        close={closeComment}
        orderId={order?.id || 0}
        comment={order?.comments || ''}
        updateOrderValue={updateOrderComments}
      />
      <WarehouseNotesModal
        modalProps={modalPropsWarehouseNotes}
        close={closeWarehouseNotes}
        orderId={order?.id || 0}
        note={order?.warehouseNote || ''}
        updateOrderValue={updateOrderWarehouseNote}
      />
      <HistoryModal
        modalProps={modalPropsHistory}
        close={closeHistory}
        orderId={order?.id || 0}
      />
      <IssueDrawer
        drawerProps={createIssueDrawerProps}
        formProps={createIssueFormProps}
        close={createIssueClose}
        orderId={order?.id}
        refetchFn={props.refetchFn}
      />
      <DecreaseDrawer
        drawerProps={editdecreaseDrawerProps}
        formProps={editdecreaseFormProps}
        close={editdecreaseClose}
        orderId={order?.id}
      />
      <RefundDrawer
        drawerProps={refundDrawerProps}
        formProps={refundDrawerFormProps}
        close={refundDrawerClose}
        order={order!}
        isPenalization={isPenalization}
        isCorrection={isCorrection}
        isRefund={isRefund}
      />
    </>
  )
}
